import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { api, ApiError } from "../lib/api";
import { SearchSelect } from "../components/SearchSelect";

type SubFile = { name: string; url: string; size: number; type: string };
type MountedReport = {
  id: number; report_url: string | null; slug: string | null; title_zh: string; title_en: string | null;
  summary_zh: string | null; summary_en: string | null; pages: number | null; sector: string | null; visibility: string;
};
type Detail = {
  id: number; title: string; note: string | null; files: SubFile[];
  status: "received" | "processing" | "done" | "rejected"; reject_reason: string | null; created_at: string;
  user_id: number; user_email: string; user_name: string; user_company: string | null;
  report: MountedReport | null;
};

const STATUS_LABEL: Record<string, string> = { received: "待处理", processing: "制作中", done: "已交付", rejected: "被驳回" };
const STATUS_COLOR: Record<string, string> = { received: "#888", processing: "#b8860b", done: "#2e8b57", rejected: "#b04a3a" };

function fmtSize(b: number) { return b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`; }

export function StudioSubmissionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [d, setD] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setError(null);
    api.get<Detail>(`/api/admin/studio/submissions/${id}`).then(setD).catch((e) => setError(String(e)));
  };
  useEffect(load, [id]);

  if (error) return <div className="error">{error}</div>;
  if (!d) return <div className="muted">加载中…</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">{d.title || "提交明细"}</div>
          <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
            {d.user_name} · {d.user_email}{d.user_company ? ` · ${d.user_company}` : ""} · {new Date(d.created_at).toLocaleString()}
            <span style={{ marginLeft: 10, color: STATUS_COLOR[d.status] }}>{STATUS_LABEL[d.status]}</span>
          </div>
        </div>
        <div className="page-actions">
          <button className="btn" onClick={() => nav("/studio/submissions")}>← 返回列表</button>
        </div>
      </div>

      {/* 附言 */}
      {d.note && (
        <div className="form" style={{ marginBottom: 16 }}>
          <div className="ce-label" style={{ marginBottom: 8 }}>附言</div>
          <div style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{d.note}</div>
        </div>
      )}

      {/* 原始资料（卡片样式，参考用户中心报告明细附件） */}
      <div className="form" style={{ marginBottom: 16 }}>
        <div className="ce-label" style={{ marginBottom: 10 }}>原始资料 · {d.files.length}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 10 }}>
          {d.files.map((f, i) => (
            <a key={i} href={f.url} target="_blank" rel="noopener noreferrer"
              style={{ border: "1px solid var(--border)", padding: "12px 14px", position: "relative", display: "block" }}>
              <div style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</div>
              <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>{fmtSize(f.size)} ↗</div>
            </a>
          ))}
        </div>
      </div>

      {/* 驳回原因（已驳回时显示） */}
      {d.status === "rejected" && d.reject_reason && (
        <div className="form" style={{ marginBottom: 16, borderLeft: "2px solid #b04a3a" }}>
          <div className="ce-label" style={{ marginBottom: 6, color: "#b04a3a" }}>驳回原因</div>
          <div style={{ fontSize: 13 }}>{d.reject_reason}</div>
        </div>
      )}

      {/* 已挂载报告成果：从已有智库报告里选一条关联（可搜索下拉） */}
      <LinkReportSection sub={d} onDone={load} />

      {/* 驳回（未挂报告、未交付、未驳回时可用） */}
      {!d.report && d.status !== "done" && d.status !== "rejected" && (
        <div style={{ marginTop: 16 }}><RejectBox sub={d} onDone={load} /></div>
      )}

      {/* 往来记录（交付后修正）：有报告即出现对话 + 回复 */}
      {d.report && <MessageThread sub={d} onChanged={load} />}
    </div>
  );
}

type LinkableReport = { id: number; title_zh: string; title_en: string | null; user_id: number | null; submission_id: number | null; visibility: string; user_name: string | null };

/**
 * 已挂载报告成果：从「智库报告」里选一条关联给本提交/用户（可搜索下拉）。
 * 可选范围（后端 linkable-reports 端点）：非珂玺自有 且（未分配用户 或 已属本用户）。
 * 选中即关联并标记提交为「已交付」；清除即解除关联并回到「制作中」。
 * 报告本身的内容（标题/链接/slug/摘要）在「智库报告」页编辑，此处不重复。
 */
function LinkReportSection({ sub, onDone }: { sub: Detail; onDone: () => void }) {
  const [reports, setReports] = useState<LinkableReport[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    api.get<LinkableReport[]>(`/api/admin/studio/submissions/${sub.id}/linkable-reports`)
      .then(setReports).catch(() => setReports([]));
  }, [sub.id]);

  const current = sub.report ? sub.report.id : null;

  const relink = async (reportId: number | string | null) => {
    if (busy || reportId === current) return;
    setBusy(true); setMsg(null);
    try {
      if (reportId === null) {
        if (current) await api.patch(`/api/admin/studio/reports/${current}`, { submission_id: null });
        await api.patch(`/api/admin/studio/submissions/${sub.id}`, { status: "processing" });
      } else {
        if (current) await api.patch(`/api/admin/studio/reports/${current}`, { submission_id: null });
        await api.patch(`/api/admin/studio/reports/${reportId}`, { submission_id: Number(sub.id), user_id: Number(sub.user_id) });
        await api.patch(`/api/admin/studio/submissions/${sub.id}`, { status: "done" });
      }
      onDone();
    } catch (e) { setMsg(e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : String(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="form">
      <div className="ce-label" style={{ marginBottom: 8 }}>已挂载报告成果</div>
      <div className="muted" style={{ fontSize: 11, marginBottom: 10 }}>
        从「智库报告」选一条关联给该用户（仅非珂玺自有、且未分配或已属本用户的报告）。选中即标记已交付；报告内容在「智库报告」页编辑、新建。
      </div>
      {reports === null
        ? <div className="muted" style={{ fontSize: 13 }}>加载中…</div>
        : <SearchSelect
            value={current}
            onChange={relink}
            placeholder="输入报告标题搜索…"
            options={reports.map((r) => ({
              value: r.id,
              label: r.title_zh || `报告 #${r.id}`,
              hint: r.user_id == null ? "未分配" : (r.user_name || `用户 ${r.user_id}`),
            }))}
          />}
      {current != null && <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>已关联并标记为「已交付」。清除选择即解除关联。</div>}
      {msg && <div className="error" style={{ fontSize: 12, marginTop: 8 }}>{msg}</div>}
    </div>
  );
}

/** 往来记录（交付后修正对话）+ 管理员回复（文字 + 可选附件）。 */
function MessageThread({ sub, onChanged }: { sub: Detail; onChanged: () => void }) {
  type Msg = { id: number; author_role: "user" | "admin"; body: string; files: SubFile[]; created_at: string };
  const [msgs, setMsgs] = useState<Msg[] | null>(null);
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = () => { api.get<Msg[]>(`/api/admin/studio/submissions/${sub.id}/messages`).then(setMsgs).catch(() => setMsgs([])); };
  useEffect(load, [sub.id]);

  const send = async () => {
    if (!body.trim()) { setErr("请填写回复内容"); return; }
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append("body", body.trim());
      files.forEach((f) => fd.append("files", f));
      await api.postForm(`/api/admin/studio/submissions/${sub.id}/messages`, fd);
      setBody(""); setFiles([]); load(); onChanged();
    } catch (e) { setErr(e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : String(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="form" style={{ marginTop: 16 }}>
      <div className="ce-label" style={{ marginBottom: 12 }}>修正往来</div>
      {msgs === null && <div className="muted" style={{ fontSize: 12 }}>加载中…</div>}
      {msgs && msgs.length === 0 && <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>暂无往来。用户发起修正后会出现在这里。</div>}
      {msgs && msgs.map((m) => {
        const admin = m.author_role === "admin";
        return (
          <div key={m.id} style={{ display: "flex", justifyContent: admin ? "flex-end" : "flex-start", marginBottom: 12 }}>
            <div style={{ maxWidth: "78%", background: admin ? "rgba(184,134,11,0.08)" : "var(--bg-soft, #f6f5f2)", border: "1px solid var(--border)", padding: "10px 12px" }}>
              <div className="muted" style={{ fontSize: 10, marginBottom: 6 }}>{admin ? "珂玺团队" : "用户"} · {new Date(m.created_at).toLocaleString()}</div>
              <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{m.body}</div>
              {m.files && m.files.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                  {m.files.map((f, i) => (
                    <a key={i} href={f.url} target="_blank" rel="noopener noreferrer" className="slug" style={{ fontSize: 11 }}>{f.name} ↗</a>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}

      <textarea placeholder="回复用户（说明修正进度 / 已更新报告）" rows={3} value={body} onChange={(e) => setBody(e.target.value)}
        style={{ width: "100%", resize: "vertical", marginTop: 8 }} />
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
        <input type="file" multiple onChange={(e) => { setFiles(Array.from(e.target.files || [])); }} />
      </div>
      {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
      <button className="btn btn-primary" style={{ marginTop: 10 }} disabled={busy} onClick={send}>{busy ? "发送中…" : "发送回复"}</button>
    </div>
  );
}

/** 驳回：2 行输入 + 加大按钮且与输入框对齐 */
function RejectBox({ sub, onDone }: { sub: Detail; onDone: () => void }) {
  const [reason, setReason] = useState(sub.reject_reason || "");
  const [busy, setBusy] = useState(false);
  const reject = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/api/admin/studio/submissions/${sub.id}`, { status: "rejected", reject_reason: reason.trim() });
      onDone();
    } finally { setBusy(false); }
  };
  return (
    <div className="form">
      <div className="ce-label" style={{ marginBottom: 10 }}>驳回此提交</div>
      <textarea placeholder="驳回原因（用户可见）" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
        style={{ width: "100%", resize: "vertical" }} />
      <button className="btn" style={{ marginTop: 10, width: "100%", height: 38 }} disabled={busy || !reason.trim()} onClick={reject}>
        {busy ? "处理中…" : "驳回"}
      </button>
    </div>
  );
}
